'use client';

import React, { useState, useEffect } from 'react';

const PipelinePointStyleEditor = ({ config, onChange, onPreview }) => {
    // --- Local State Management ---
    const [pipelineConfig, setPipelineConfig] = useState({});

    // --- Effect to Sync Local State with Incoming Config Prop ---
    // This runs ONLY when the config prop from the parent changes.
    useEffect(() => {
        console.log("PipelineEditor: Syncing local state with new config prop:", config);
        const initialConfig = config || {}; // Ensure config is an object
        setPipelineConfig(initialConfig);
    }, [config]); // Dependency: Only the incoming config prop

    // --- Event Handlers ---

    // Helper to update local config and auto-preview changes
    const updateLocalConfig = (newValues) => {
        const updatedLocalConfig = {
            ...pipelineConfig,
            ...newValues,
            type: 'pipe' // Always ensure type is correct
        };
        setPipelineConfig(updatedLocalConfig); // Update local state

        // Auto-preview changes on the map - preserve labels during preview
        if (onPreview) {
            onPreview({ ...updatedLocalConfig, preserveLabels: true });
        }
        // Also notify parent of changes
        if (onChange) {
            onChange(updatedLocalConfig);
        }
    };

    const handleSymbolStyleChange = (e) => {
        updateLocalConfig({
            symbol: { ...pipelineConfig.symbol, style: e.target.value }
        });
    };

    const handleSymbolSizeChange = (e) => {
        updateLocalConfig({
            symbol: { ...pipelineConfig.symbol, size: Number(e.target.value) }
        });
    };

    const handleOutlineWidthChange = (e) => {
        updateLocalConfig({
            symbol: {
                ...pipelineConfig.symbol,
                outline: { ...pipelineConfig.symbol?.outline, width: Number(e.target.value) }
            }
        });
    };

    const handleOutlineColorChange = (e) => {
        updateLocalConfig({
            symbol: {
                ...pipelineConfig.symbol,
                outline: { ...pipelineConfig.symbol?.outline, color: e.target.value }
            }
        });
    };

    // --- Render ---
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Pipeline Visualization Settings
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Configure how pipeline projects are displayed based on their status.
                </p>
            </div>

            {/* Symbol configuration */}
            <div>
                <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">
                    Marker Style
                </h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Shape */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Shape
                        </label>
                        <select
                            value={pipelineConfig.symbol?.style || 'circle'}
                            onChange={handleSymbolStyleChange}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="circle">Circle</option>
                            <option value="square">Square</option>
                            <option value="diamond">Diamond</option>
                            <option value="triangle">Triangle</option>
                            <option value="cross">Cross</option>
                        </select>
                    </div>

                    {/* Size */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Size
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="30"
                            value={pipelineConfig.symbol?.size || 12}
                            onChange={handleSymbolSizeChange}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    {/* No Outline Checkbox */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Outline
                        </label>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="noOutlinePipe"
                                checked={pipelineConfig.symbol?.outline?.width === 0 || pipelineConfig.symbol?.outline?.width === undefined}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        updateLocalConfig({
                                            symbol: {
                                                ...pipelineConfig.symbol,
                                                outline: { ...pipelineConfig.symbol?.outline, width: 0 }
                                            }
                                        });
                                    } else {
                                        updateLocalConfig({
                                            symbol: {
                                                ...pipelineConfig.symbol,
                                                outline: { ...pipelineConfig.symbol?.outline, width: 1 }
                                            }
                                        });
                                    }
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="noOutlinePipe" className="ml-2 text-sm text-gray-700 dark:text-gray-300">No Outline</label>
                        </div>
                    </div>

                    {/* Outline Width - only show if outline is enabled */}
                    {pipelineConfig.symbol?.outline?.width !== 0 && pipelineConfig.symbol?.outline?.width !== undefined && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Outline Width
                                </label>
                                <input
                                    type="number"
                                    min="0.5"
                                    max="5"
                                    step="0.5"
                                    value={pipelineConfig.symbol?.outline?.width ?? 1}
                                    onChange={handleOutlineWidthChange}
                                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Outline Color */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Outline Color
                                </label>
                                <input
                                    type="color"
                                    value={pipelineConfig.symbol?.outline?.color || '#FFFFFF'}
                                    onChange={handleOutlineColorChange}
                                    className="block h-10 w-full"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PipelinePointStyleEditor;